/**
 * SWR Global Configuration Provider
 *
 * Provides global SWR configuration for the entire app including:
 * - Cache settings
 * - Revalidation behavior
 * - Error retry logic with exponential backoff
 * - Request deduplication
 *
 * @see https://swr.vercel.app/docs/global-configuration
 */

'use client'

import { SWRConfig } from 'swr'

const isDev = process.env.NODE_ENV === 'development'
import type { SWRConfiguration } from 'swr'

/**
 * Global SWR configuration
 *
 * These settings apply to all useSWR hooks unless overridden locally
 */
const swrConfig: SWRConfiguration = {
  // --- Cache & Revalidation Settings ---

  // Sound data changes at most weekly — don't re-query on every tab focus
  revalidateOnFocus: false,

  // Revalidate when network reconnects
  revalidateOnReconnect: true,

  // Don't revalidate on mount if data is already cached
  revalidateIfStale: false,

  // --- Performance Settings ---

  // Dedupe identical requests within 2 seconds
  // This prevents duplicate API calls when multiple components request the same data
  dedupingInterval: 2000,

  // Show previous data while revalidating (better UX)
  keepPreviousData: true,

  // --- Error Handling & Retry Logic ---

  // Custom error retry with exponential backoff
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    // Don't retry on 404 errors (resource not found)
    if (error.status === 404) return

    // Don't retry on 401/403 errors (auth errors)
    if (error.status === 401 || error.status === 403) return

    // Max 3 retries
    if (retryCount >= 3) return

    // Exponential backoff: 1s, 2s, 4s
    const timeout = Math.min(1000 * Math.pow(2, retryCount), 10000)

    setTimeout(() => {
      revalidate({ retryCount })
    }, timeout)
  },

  // Report errors to Sentry and show detailed messages in development
  onError: (error, key) => {
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, { tags: { context: 'SWR' }, extra: { key } })
    }).catch(() => {})
    if (isDev) console.error('[SWR Error]', { key, error })
  },

  // --- Success Callback ---

  onSuccess: (data, key) => {
    if (isDev) console.log('[SWR Success]', { key, dataCount: Array.isArray(data) ? data.length : 'single' })
  },

  // --- Fetcher Function ---

  // The fetcher is defined in individual hooks for type safety
  // This could be set here if all API calls follow the same pattern
  // fetcher: (url) => fetch(url).then(r => r.json()),

  // --- Cache Settings ---

  // Provider for custom cache (useful for SSR or testing)
  // We use the default in-memory cache for client-side
  // provider: () => new Map(),

  // --- Suspense & Error Boundaries ---

  // Disable suspense mode by default (opt-in per hook)
  suspense: false,

  // --- Focus Revalidation Settings ---

  // Time window for focus revalidation (5 minutes)
  // If data was fetched within this window, don't revalidate on focus
  focusThrottleInterval: 5 * 60 * 1000, // 5 minutes
}

/**
 * SWR Provider Component
 *
 * Wraps the app with SWR configuration context
 *
 * @example
 * ```tsx
 * // In app/layout.tsx
 * <SWRProvider>
 *   <App />
 * </SWRProvider>
 * ```
 */
export function SWRProvider({ children }: { children: React.ReactNode }) {
  return (
    <SWRConfig value={swrConfig}>
      {children}
    </SWRConfig>
  )
}

/**
 * Export the config for testing or custom usage
 */
// eslint-disable-next-line react-doctor/only-export-components
export { swrConfig }
